import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { C } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import PatientSearchBar from "./ipd-billing/components/PatientSearchBar";
import PaymentsReceiptsPanel from "./ipd-billing/components/PaymentsReceiptsPanel";
import {
  resolveAdmissionId,
  persistAdmissionContext,
  restoreAdmissionContext,
  hydrateRestoredPatient,
  clearAdmissionContext,
  listAllocatedAdmissions,
  admissionToPatientContext,
  listPayments,
} from "./ipd-billing/billing-api";

// ─────────────────────────────────────────────────────────────────────────────
// Refund & Cancel — dedicated screen for the Payments / Receipts panel
// (receipt cancel → refund / transfer to deposit). Patient-scoped like the
// other billing screens.
// ─────────────────────────────────────────────────────────────────────────────
export default function IPDBillingRefundCancel() {
  const location = useLocation();
  const passedPatient = location.state?.patient || null;

  const [activeAdmissionId, setActiveAdmissionId] = useState(() => resolveAdmissionId(passedPatient));
  const [selectedPatient, setSelectedPatient] = useState(() => {
    if (passedPatient?.admission_id || passedPatient?.admissionId) return passedPatient;
    return restoreAdmissionContext();
  });
  const [admissions, setAdmissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingAdmissions, setLoadingAdmissions] = useState(false);
  const [searchError, setSearchError] = useState("");

  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [apiMessage, setApiMessage] = useState("");

  const hasPatient = Boolean(activeAdmissionId);

  const loadAdmissions = useCallback(async () => {
    setLoadingAdmissions(true);
    setSearchError("");
    try {
      setAdmissions(await listAllocatedAdmissions("Allocated"));
    } catch (err) {
      setSearchError(err.message || "Failed to load patients");
      setAdmissions([]);
    } finally {
      setLoadingAdmissions(false);
    }
  }, []);

  useEffect(() => { loadAdmissions(); }, [loadAdmissions]);

  // Fill in an id-only restore (name/bed/doctor/payer) once the list lands.
  useEffect(() => {
    const hydrated = hydrateRestoredPatient(selectedPatient, admissions);
    if (hydrated !== selectedPatient) {
      persistAdmissionContext(hydrated);
      setSelectedPatient(hydrated);
    }
  }, [admissions, selectedPatient]);

  useEffect(() => {
    if (passedPatient) {
      const id = resolveAdmissionId(passedPatient);
      if (id) {
        persistAdmissionContext(passedPatient);
        setActiveAdmissionId(id);
        setSelectedPatient(passedPatient);
      }
    }
  }, [passedPatient]);

  const refreshPayments = useCallback(async () => {
    if (!activeAdmissionId) { setPayments([]); return; }
    setIsLoading(true);
    setApiError("");
    try {
      setPayments(await listPayments(activeAdmissionId));
    } catch (err) {
      setApiError(err.message || "Failed to load payments");
      setPayments([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeAdmissionId]);

  useEffect(() => { refreshPayments(); }, [refreshPayments]);

  const handleSelectAdmission = useCallback((row) => {
    const patient = admissionToPatientContext(row);
    persistAdmissionContext(patient);
    setSelectedPatient(patient);
    setActiveAdmissionId(String(row.admission_id));
    setSearchQuery("");
    setApiError("");
    setApiMessage("");
  }, []);

  const handleClearPatient = useCallback(() => {
    setSelectedPatient(null);
    setActiveAdmissionId("");
    clearAdmissionContext();
    setSearchQuery("");
    setPayments([]);
    setApiError("");
    setApiMessage("Search and select a patient to manage refunds & cancellations.");
  }, []);

  return (
    <IPDBillingLayout activeNav="refund-cancel">
      <div style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <PatientSearchBar
          admissions={admissions}
          loadingAdmissions={loadingAdmissions}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSelect={handleSelectAdmission}
          selectedPatient={selectedPatient}
          onClear={handleClearPatient}
          onRefresh={loadAdmissions}
          searchError={searchError}
          selectHint="select one to manage refunds & cancellations"
        />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: "22px", fontWeight: "800", color: C.text }}>Refund &amp; Cancel</h2>
            <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>
              {hasPatient ? `Payments & receipts · Admission ${activeAdmissionId}` : "Select a patient above to view payments & receipts"}
            </p>
            {apiError && <p style={{ margin: "6px 0 0", fontSize: "12px", color: C.danger, fontWeight: "600" }}>{apiError}</p>}
            {apiMessage && !apiError && <p style={{ margin: "6px 0 0", fontSize: "12px", color: C.success, fontWeight: "600" }}>{apiMessage}</p>}
          </div>
          <button
            type="button"
            onClick={refreshPayments}
            disabled={!hasPatient || isLoading}
            style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "9px 16px", background: "#fff", color: C.text, border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: !hasPatient || isLoading ? "not-allowed" : "pointer", opacity: hasPatient ? 1 : 0.55 }}
          >
            <SvgIcon name="refresh" size={15} color={C.text} /> {isLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {!hasPatient ? (
          <div style={{ background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, padding: "48px 32px", textAlign: "center" }}>
            <div style={{ fontSize: "15px", fontWeight: "700", color: C.text, marginBottom: "8px" }}>No patient selected</div>
            <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>Search and select an allocated IPD patient to view their receipts and process cancellations, refunds or deposit transfers.</p>
          </div>
        ) : (
          <div style={{ background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
            <h3 style={{ margin: 0, padding: "18px 20px 4px", fontSize: "14px", fontWeight: "700", color: C.text }}>Payments / Receipts</h3>
            {isLoading ? (
              <div style={{ padding: "24px 20px", color: C.textSub, fontSize: "13px" }}>Loading payments…</div>
            ) : (
              <PaymentsReceiptsPanel
                admissionId={activeAdmissionId}
                payments={payments}
                onRefresh={refreshPayments}
                notify={(msg, isError) => { if (isError) { setApiError(msg); setApiMessage(""); } else { setApiMessage(msg); setApiError(""); } }}
              />
            )}
          </div>
        )}
      </div>
    </IPDBillingLayout>
  );
}
